import * as React from 'react';
import InputHandler from 'Components/InputHandler';
import { seizureType } from 'neuro-schemas';
import { useIntl } from 'react-intl';

const allClassifications = seizureType.utils.classificationOptions as TSeizureClassification[];

const formatOptionIndent = (o: string): React.ReactNode => {
  const partialIndentRegex = /^[0-9]\.([0-9])*\.*([0-9])*\.*/;
  const matches = o.match(partialIndentRegex);
  return <span style={{ marginLeft: ((matches || []).filter(m => m).length - 1) * 1.5 + 'rem' }}>{o}</span>;
};

const options = allClassifications.map(sc => {
  const pieces = sc.split('.');
  let group = pieces.length > 2 ? [pieces[0], pieces[1]].join('.') + '.' : pieces[0];
  const findChildClassification = allClassifications.filter(c => c.match('^' + sc));
  if (findChildClassification.length > 1) {
    if (sc.split('.').length === 2) group = sc;
  }
  return { classification: sc, group };
});

const strongRegex = /<\/*strong>/g;

const SeizureClassificationComponent = ({
  item,
  i,
  criteria,
  onChangeCriteria,
  multiple,
  limit,
}: IOwnProps): React.JSX.Element => {
  const { formatMessage } = useIntl();
  const fm = (id: string, values?: Record<string, (chunks: string[]) => React.JSX.Element>) =>
    formatMessage({ id }, values);

  const filteredOptions = options.filter(o => !criteria.includes(o.classification));

  const getOptionLabel = (o: { classification: NonNullable<TSeizureClassification | undefined>; group: string }) =>
    o.classification
      ? `${o.classification} ${formatMessage({ id: `seizure.opts.${o.classification}` }).replace(strongRegex, '')}`
      : '';

  const renderOption = (o: { classification: NonNullable<TSeizureClassification | undefined>; group: string }) =>
    o && (
      <div key={o.classification} style={{ display: 'flex' }}>
        <span style={{ display: 'inline' }}>{formatOptionIndent(o.classification)}&nbsp;</span>
        <span style={{ display: 'inline' }}>
          {fm(`seizure.opts.${o.classification}`, {
            strong: (chunks: string[]) => (
              <span key={chunks.join('')} style={{ fontWeight: '600', display: 'inline' }}>
                {React.Children.toArray(chunks)}
              </span>
            ),
          })}
        </span>
      </div>
    );

  const groupBy = (option: Record<string, string>) => {
    if (option['group'].split('.').length === 1) return '';
    return `${option['group']} ${formatMessage({ id: `seizure.opts.${option['group']}` }).replace(strongRegex, '')}`;
  };

  const renderGroup = (params: { key: string | number; group: string; children?: React.ReactNode }) => (
    <li key={params.key}>
      <ul style={{ margin: 0, padding: '0 0 0 1rem' }}>{params.children}</ul>
    </li>
  );

  if (multiple) {
    const normalizedItems = Array.isArray(item) ? item : item ? [item] : [];
    const docValue = normalizedItems
      .map(c => options.find(o => o.classification === c))
      .filter((o): o is (typeof options)[0] => Boolean(o));

    const handleChange = (values: TOnChangeValues) => {
      const arr = values['seizureClassification'] as Array<{ classification: TSeizureClassification; group: string }>;
      onChangeCriteria(i)({ seizureClassification: arr ?? [] });
    };

    return (
      <InputHandler
        type="AutoCompleteSelectAndAdd"
        name="seizureClassification"
        editing={true}
        formData={{ onChange: handleChange, document: { seizureClassification: docValue } }}
        options={filteredOptions}
        placeholder="extendedSearch.seizureClassification"
        width={30}
        getOptionLabel={getOptionLabel}
        groupID="group"
        renderOption={renderOption}
        groupBy={groupBy}
        renderGroup={renderGroup}
        limit={limit}
      />
    );
  }

  return (
    <InputHandler
      type="AutoCompleteSelect"
      name="seizureClassification"
      editing={true}
      formData={{
        onChange: onChangeCriteria(i),
        document: { seizureClassification: item ? { classification: item } : null },
      }}
      options={filteredOptions}
      placeholder="extendedSearch.seizureClassification"
      width={30}
      getOptionLabel={getOptionLabel}
      groupID="group"
      renderOption={renderOption}
      groupBy={groupBy}
      renderGroup={renderGroup}
    />
  );
};

interface IOwnProps {
  item: TSeizureClassification | TSeizureClassification[] | undefined;
  i: number;
  criteria: Array<TSeizureClassification | undefined>;
  onChangeCriteria: (i: number) => (values: TOnChangeValues) => void;
  multiple?: boolean;
  limit?: number;
}

export default SeizureClassificationComponent;
